import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  BriefcaseIcon,
  CalendarIcon,
  ChevronLeftIcon,
  CopyIcon,
  ListIcon,
  PencilIcon,
  RepeatIcon,
} from 'lucide-react';
import { parseAsStringLiteral, useQueryState } from 'nuqs';

import type { CalendarView } from '@/components/calendar/types';
import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import { ViewToggle } from '@/components/ui/view-toggle';
import { useCurrentBusiness } from '@/hooks/use-current-business';
import { useEntitlements } from '@/hooks/use-entitlements';
import { useRole } from '@/hooks/use-role';
import { createCustomer } from '@/lib/api/customers';
import {
  createJob,
  getJob,
  getJobSummary,
  jobQueryKey,
  jobSummaryQueryKey,
  jobVisitsQueryKey,
  listJobVisits,
  updateJob,
  type UpdateJobRequest,
} from '@/lib/api/jobs';
import { listPaymentMethods, paymentMethodsQueryKey } from '@/lib/api/payment-methods';

import { AddJobModal } from '../jobs/add-job-modal';
import { formatDate, resolvePaymentMethodLabel } from '../jobs/helpers';
import { JobAttachmentsSection } from '../jobs/job-attachments-section';
import { JobDetailModal } from '../jobs/job-detail-modal';
import { JobNotesSection } from '../jobs/job-notes-section';
import { JobInvoicesSection } from '../jobs/job-invoices-section';
import { JobQuotesSection } from '../jobs/job-quotes-section';
import type { NewJobSubmission } from '../jobs/types';
import { filterVisits, initialVisitMonth, rosterFromSummary } from './helpers';
import { SeriesCrewBar } from './series-crew-bar';
import { SeriesSummaryStrip } from './series-summary-strip';
import type { VisitFilterState } from './types';
import { VisitCalendar } from './visit-calendar';
import { VisitFilterBar } from './visit-filter-bar';
import { VisitRow } from './visit-row';
import { VisitTable } from './visit-table';

const DEFAULT_FILTERS: VisitFilterState = {
  status: 'all',
  payment: 'all',
  date: 'all',
};

const VIEW_PARAM = parseAsStringLiteral(['table', 'calendar'] as const).withDefault(
  'table',
);

export function JobDetail() {
  const { t } = useLanguage();
  const { jobId = '' } = useParams();
  const navigate = useNavigate();
  const business = useCurrentBusiness();
  const businessId = business?.business_id;
  const { isOwner, hasPermission } = useRole();
  const canManageQuotes = hasPermission('create_quotes');
  const canManageInvoices = hasPermission('create_invoices');
  const { has, isPending: entitlementsPending } = useEntitlements();
  const canRecur = has('recurring_jobs');

  const [filters, setFilters] = useState<VisitFilterState>(DEFAULT_FILTERS);
  const [editing, setEditing] = useState(false);
  const [copying, setCopying] = useState(false);
  const [visitView, setVisitView] = useQueryState('view', VIEW_PARAM);
  const [monthOverride, setMonthOverride] = useState<Date | null>(null);
  const [calView, setCalView] = useState<CalendarView>('month');

  const ready = Boolean(businessId && jobId);

  const jobQuery = useQuery({
    queryKey: jobQueryKey(businessId ?? '', jobId),
    queryFn: () => getJob(businessId!, jobId),
    enabled: ready,
  });

  const summaryQuery = useQuery({
    queryKey: jobSummaryQueryKey(businessId ?? '', jobId),
    queryFn: () => getJobSummary(businessId!, jobId),
    enabled: ready && isOwner,
  });

  const visitsQuery = useQuery({
    queryKey: jobVisitsQueryKey(businessId ?? '', jobId),
    queryFn: () => listJobVisits(businessId!, jobId),
    enabled: ready && isOwner,
  });

  const paymentMethodsQuery = useQuery({
    queryKey: paymentMethodsQueryKey(businessId ?? ''),
    queryFn: () => listPaymentMethods(businessId!),
    enabled: ready,
  });

  const updateMutation = useMutation({
    mutationFn: (body: UpdateJobRequest) => updateJob(businessId!, jobId, body),
    meta: { successMessage: t.jobUpdated },
    onSuccess: () => setEditing(false),
  });

  const copyMutation = useMutation({
    mutationFn: async (submission: NewJobSubmission) => {
      let customerUserId = submission.customerUserId;

      if (submission.mode === 'new') {
        const created = await createCustomer(businessId!, {
          full_name: submission.newCustomerName!,
          email: submission.newCustomerEmail || undefined,
          phone: submission.newCustomerPhone || undefined,
        });
        if (!created.customer_user_id) throw new Error(t.jobCustomerNoAccount);
        customerUserId = created.customer_user_id;
      }

      return createJob(businessId!, {
        ...submission.job,
        customer_user_id: customerUserId!,
      });
    },
    meta: { successMessage: t.jobAdded },
    onSuccess: (created) => {
      setCopying(false);
      navigate(`/jobs/${created.id}`);
    },
  });

  const visits = useMemo(() => visitsQuery.data ?? [], [visitsQuery.data]);
  const filtered = useMemo(
    () => filterVisits(visits, filters),
    [visits, filters],
  );
  const visitMonth = useMemo(
    () => monthOverride ?? initialVisitMonth(summaryQuery.data, visits),
    [monthOverride, summaryQuery.data, visits],
  );

  const job = jobQuery.data;
  // A recurring job always gets the full timeline; a one-time job does too
  // when the tier is entitled, otherwise it falls back to a single-visit
  // panel (visit management is gated — see plan §2).
  const fullView = canRecur || Boolean(job?.frequency);

  const backLink = (
    <Link
      to="/jobs"
      className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 w-fit"
      data-testid="series-back"
    >
      <ChevronLeftIcon className="w-3.5 h-3.5" />
      {t.seriesBack}
    </Link>
  );

  const defaultPaymentMethodLabel = job
    ? resolvePaymentMethodLabel(
        t,
        paymentMethodsQuery.data,
        job.default_payment_method,
        job.default_payment_method_id,
      )
    : null;

  const header = (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
          {job?.frequency ? (
            <RepeatIcon className="w-5 h-5 text-primary" />
          ) : (
            <BriefcaseIcon className="w-5 h-5 text-primary" />
          )}
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold leading-tight truncate">
            {job?.title ?? t.loading}
          </h1>
          {job && (
            <p className="text-xs text-muted-foreground">
              {formatDate(job.start_date)}
              {defaultPaymentMethodLabel && <>{' · '}{defaultPaymentMethodLabel}</>}
            </p>
          )}
        </div>
      </div>
      {isOwner && job && (
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => setCopying(true)}
            data-testid="copy-job-open"
          >
            <CopyIcon className="w-3.5 h-3.5" />
            {t.jobCopyJob}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => setEditing(true)}
            data-testid="edit-job-open"
          >
            <PencilIcon className="w-3.5 h-3.5" />
            {t.editJob}
          </Button>
        </div>
      )}
    </div>
  );

  let visitsBody: React.ReactNode = null;
  const summary = summaryQuery.data;
  if (isOwner) {
    if (entitlementsPending || summaryQuery.isPending || visitsQuery.isPending) {
      visitsBody = (
        <p className="text-sm text-muted-foreground py-10">{t.loading}</p>
      );
    } else if (summaryQuery.isError || visitsQuery.isError || !summary) {
      visitsBody = (
        <p className="text-sm text-destructive">{t.seriesLoadError}</p>
      );
    } else if (fullView) {
      visitsBody = (
        <div className="flex flex-col gap-5">
          <SeriesSummaryStrip summary={summary} job={job} />
          <SeriesCrewBar
            businessId={businessId!}
            jobId={jobId}
            roster={rosterFromSummary(summary.labor.by_employee)}
          />
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <VisitFilterBar
                filters={filters}
                count={filtered.length}
                onChange={(patch) => setFilters((f) => ({ ...f, ...patch }))}
              />
              <ViewToggle
                value={visitView}
                onChange={(next) => void setVisitView(next)}
                aria-label={t.seriesColDate}
                options={[
                  {
                    value: 'table',
                    label: t.tableView,
                    icon: ListIcon,
                    testId: 'view-toggle-table',
                  },
                  {
                    value: 'calendar',
                    label: t.calendarView,
                    icon: CalendarIcon,
                    testId: 'view-toggle-calendar',
                  },
                ]}
              />
            </div>
            {visitView === 'calendar' ? (
              <VisitCalendar
                visits={filtered}
                date={visitMonth}
                view={calView}
                onNavigate={setMonthOverride}
                onView={setCalView}
                businessId={businessId!}
                jobId={jobId}
                jobTitle={job?.title ?? ''}
                jobDefaultPaymentMethod={job?.default_payment_method}
                jobDefaultPaymentMethodId={job?.default_payment_method_id}
              />
            ) : (
              <VisitTable
                visits={filtered}
                businessId={businessId!}
                jobId={jobId}
                jobTitle={job?.title ?? ''}
                jobDefaultPaymentMethod={job?.default_payment_method}
                jobDefaultPaymentMethodId={job?.default_payment_method_id}
              />
            )}
          </div>
        </div>
      );
    } else if (visits.length > 0) {
      // One-time job on an un-entitled tier: just the single visit.
      visitsBody = (
        <ul className="flex flex-col gap-2" data-testid="single-visit-panel">
          <VisitRow
            visit={visits[0]}
            businessId={businessId!}
            jobId={jobId}
            jobTitle={job?.title ?? ''}
            jobDefaultPaymentMethod={job?.default_payment_method}
            jobDefaultPaymentMethodId={job?.default_payment_method_id}
          />
        </ul>
      );
    }
  }

  return (
    <div className="max-w-5xl mx-auto flex flex-col gap-5">
      {backLink}
      {header}

      {jobQuery.isError && (
        <p className="text-sm text-destructive">{t.noJobsFound}</p>
      )}

      {visitsBody}

      {job && businessId && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <JobNotesSection businessId={businessId} jobId={jobId} />
          <JobAttachmentsSection businessId={businessId} jobId={jobId} />
          {(isOwner || canManageQuotes) && (
            <JobQuotesSection
              businessId={businessId}
              jobId={jobId}
              jobTitle={job.title}
            />
          )}
          {(isOwner || canManageInvoices) && (
            <JobInvoicesSection
              businessId={businessId}
              jobId={jobId}
              jobTitle={job.title}
              jobDefaultPaymentMethod={job.default_payment_method}
              jobDefaultPaymentMethodId={job.default_payment_method_id}
            />
          )}
        </div>
      )}

      {editing && businessId && (
        <JobDetailModal
          businessId={businessId}
          jobId={jobId}
          onClose={() => setEditing(false)}
          onSave={(body) => updateMutation.mutate(body)}
          isSaving={updateMutation.isPending}
        />
      )}

      {copying && businessId && job && (
        <AddJobModal
          businessId={businessId}
          sourceJob={job}
          onClose={() => setCopying(false)}
          onSave={(submission) => copyMutation.mutate(submission)}
          isSaving={copyMutation.isPending}
        />
      )}
    </div>
  );
}
